import Paper from "@mui/material/Paper";
import { Button, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import { useSelector } from "react-redux";
import { CustomDateRangePicker } from "../../../../ReusableDateRangePickerUI/CustomDateRangePicker";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { isGreaterEndDate } from "../../../../../utils/helpers";
import ReceiptsTable from "./ReceiptsTable";
import {
  setAllCurrencies,
  setIsReceiptLoading,
  setPairingRate,
  setReceiptData,
  setSelectedReceiptDateRange,
  setSelectedRow,
} from "../../../../../features/financeReceiptSlice";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import store from "../../../../../store";
import ReceiptsSkeleton from "./ReceiptsSkeleton";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { ParingRateQueryID } from "../helpers/funcs/staticQID";

const ReceiptsGetData = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const { selectedReceiptDateRange, receiptData, isReceiptLoading } =
    useSelector((state: any) => state.receipts);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const response = useSelector(selectUserData);
  const permission = response?.data?.website_permissions?.[0];
  const siteId = permission ? Object.keys(permission)[0] : "";

  // Initialize date range from URL params on component mount
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    let from = params.get("from");
    let to = params.get("to");

    if (!from || !to) {
      const prevMonth = dayjs().subtract(1, "month");
      from = prevMonth.startOf("month").format("YYYY-MM-DD");
      to = prevMonth.endOf("month").format("YYYY-MM-DD");

      params.set("from", from);
      params.set("to", to);

      if (!params.has("page")) {
        params.set("page", "0");
      }

      setSearchParams(params, { replace: true });
    }

    dispatch(setSelectedReceiptDateRange([dayjs(from), dayjs(to)]));

    handleGetReceipts(from, to);
    // eslint-disable-next-line
  }, []);
  const handleGetReceipts = async (from?: string, to?: string) => {
    const from_date = from || selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
    const to_date = to || selectedReceiptDateRange[1]?.format("YYYY-MM-DD");

    try {
      dispatch(setIsReceiptLoading(true));
      setSearchParams(
        (prevParams) => {
          const params = new URLSearchParams(prevParams);
          params.set("from", from_date);
          params.set("to", to_date);
          if (!params.has("page")) {
            params.set("page", "0");
          }
          return params;
        },
        { replace: true },
      );

      await ReceiptsEndPointLogic.getAllReceiptData(
        oauthTokenHeader,
        from_date,
        to_date,
      );
      await ReceiptsEndPointLogic.getPairingRate(
        oauthTokenHeader,
        ParingRateQueryID,
        from_date,
        to_date,
        siteId,
      );
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.parsedText || "Something went wrong from the getdata",
          type: "error",
        }),
      );
    } finally {
      dispatch(setIsReceiptLoading(false));
    }
  };

  // When the component will unmount just clear the selectedReceiptDateRange
  useEffect(() => {
    return () => {
      store.dispatch(setSelectedReceiptDateRange([]));
      store.dispatch(setReceiptData(null));
      store.dispatch(setSelectedRow([]));
      store.dispatch(setPairingRate(null));
      store.dispatch(setAllCurrencies(null));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      searchParams.has("receipt_shared_uid") ||
      searchParams.has("company_shared_uid")
    ) {
      const params = new URLSearchParams(searchParams);
      params.delete("receipt_shared_uid");
      params.delete("company_shared_uid");
      params.delete("filters");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Paper sx={{ mb: 1 }}>
        <Box sx={{ padding: " 16px " }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={7}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Receipts
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                Monitor receipts, linkage status, and partner payments.
              </Typography>
            </Grid>
            <Grid
              size={5}
              container
              spacing={2}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              <Box sx={{ width: "300px" }}>
                <CustomDateRangePicker
                  isRequired={true}
                  dataTracker="525"
                  value={selectedReceiptDateRange}
                  onChange={(newValue: any) => {
                    dispatch(setReceiptData(null));
                    const [startDate, endDate] = newValue || [];

                    if (
                      startDate &&
                      endDate &&
                      isGreaterEndDate({ startDate, endDate })
                    ) {
                      dispatch(
                        setDisplayErrorSnackBar({
                          message: "Start date cannot be after end date",
                          type: "error",
                        }),
                      );
                      return;
                    }
                    dispatch(setSelectedReceiptDateRange([startDate, endDate]));
                  }}
                />
              </Box>
              <Button
                disabled={
                  !selectedReceiptDateRange[0] ||
                  !selectedReceiptDateRange[1] ||
                  isReceiptLoading
                }
                variant="contained"
                color="secondary"
                data-tracker="524"
                sx={{ whiteSpace: "nowrap" }}
                onClick={() => handleGetReceipts()}
              >
                Get Data
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      {isReceiptLoading ? (
        <ReceiptsSkeleton />
      ) : receiptData !== null ? (
        <ReceiptsTable />
      ) : null}
    </>
  );
};
export default ReceiptsGetData;
